import { Link, useLoaderData, type LoaderFunctionArgs } from 'react-router';
import { api } from '@/lib/api';
import { fmtAgo, displayName } from '@/lib/format';
import { SegmentForm } from './segment-form';
import { DeleteSegmentButton, EvaluateButton, ImportCsv } from './segment-actions';
import { TierBadge } from '@/components/tier-badge';
import { RowLink } from '@/components/row-link';

type Segment = { id: string; name: string; description: string | null; kind: string; memberCount: number; lastEvaluatedAt: string | null; rules: unknown; system: boolean };
type Contact = { id: string; email: string; firstName: string | null; lastName: string | null; tier: string; subStatus: string | null };

export async function segmentLoader({ params }: LoaderFunctionArgs) {
  const [segment, members] = await Promise.all([
    api<Segment>(`/segments/${params.id}`),
    api<{ items: Contact[]; total: number }>(`/contacts?segmentId=${params.id}&pageSize=25`),
  ]);
  return { segment, members };
}

export function SegmentPage() {
  const { segment, members } = useLoaderData<typeof segmentLoader>();

  return (
    <div className="space-y-5">
      <Link to="/segments" className="ff-link text-sm">
        ← Segments
      </Link>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl">{segment.name}</h1>
          <p className="text-sm text-muted-foreground">
            {segment.kind} · {segment.memberCount} members · evaluated {fmtAgo(segment.lastEvaluatedAt)}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {segment.kind === 'dynamic' && <EvaluateButton id={segment.id} />}
          {!segment.system && <DeleteSegmentButton id={segment.id} />}
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-[1fr_420px]">
        <div className="space-y-5">
          <div className="overflow-x-auto rounded-lg border bg-white">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Plan</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {members.items.length === 0 && (
                  <tr>
                    <td colSpan={3} className="py-6 text-center text-muted-foreground">
                      No members yet
                    </td>
                  </tr>
                )}
                {members.items.map((c) => (
                  <RowLink key={c.id} href={`/contacts/${c.id}`}>
                    <td>
                      <Link to={`/contacts/${c.id}`} className="ff-link">
                        {displayName(c)}
                      </Link>
                      <div className="text-xs text-muted-foreground">{c.email}</div>
                    </td>
                    <td>
                      <TierBadge tier={c.tier} />
                    </td>
                    <td>{c.subStatus ?? '—'}</td>
                  </RowLink>
                ))}
              </tbody>
            </table>
            {members.total > members.items.length && (
              <div className="p-3 text-sm">
                <Link to={`/contacts?segmentId=${segment.id}`} className="ff-link">
                  View all {members.total} members →
                </Link>
              </div>
            )}
          </div>
          {segment.kind === 'static' && <ImportCsv segmentId={segment.id} />}
        </div>
        <SegmentForm key={segment.id} initial={segment} />
      </div>
    </div>
  );
}
